
import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Button, Card, Heading, Stack, Text } from '@client/shared/design';
import { GraphQLRequestError } from '@client/shared/graphql';
import { reportClientError } from '@client/shared/lib/report-client-error.ts';
import styles from '@client/shared/design/AppStates.module.css';

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  error: Error | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  override state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('Unhandled client error', error, info.componentStack);
    reportClientError('error-boundary', error);
  }

  private reset = () => this.setState({ error: null });

  override render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;

    const correlationId =
      error instanceof GraphQLRequestError ? error.correlationId : undefined;

    return (
      <div className={styles.center}>
        <Card pad="lg" raised spine className={styles.errorCard}>
          <Stack gap={4}>
            <Stack gap={2}>
              <Heading level={3}>Something went sideways.</Heading>
              <Text tone="soft">
                An unexpected error interrupted the page. Your session is still safe: try again,
                and if it keeps happening, share the reference below with support.
              </Text>
            </Stack>
            {correlationId && (
              <Text className={styles.correlation}>Reference: {correlationId}</Text>
            )}
            <div>
              <Button onClick={this.reset}>Try again</Button>
            </div>
          </Stack>
        </Card>
      </div>
    );
  }
}
